(ns frontend.view.entity-viewers.entity-editor
  (:require
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.schema.calculated-field :as calculated-field]
   [common.schema.constraints :as constraints]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [frontend.actions.account :as account-actions]
   [frontend.actions.entity :as entity-actions]
   [frontend.components.field-editor :as field-editor]
   [frontend.db :as app-db]
   [frontend.intelligence.icon :as intelligence-icon]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.view :as options-management]
   [potpuri.core :refer [dissoc-in]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [common.util :as cu]))

(defn resolve-template
  [{:keys [entity tenant-schema pipeline-id
           remove-fields override-editor-types read-only-fields]}]
  (let [entity-type (:entity/type entity)
        entity-schema (registry/get-schema tenant-schema entity-type)]
    (->> (case entity-type
           :entity.type/fund-commitment (dissoc entity-schema :fund-commitment/overview)
           :entity.type/fundraising-opportunity (dissoc entity-schema
                                                        :fundraising-opportunity/description)
           :entity.type/sales-opportunity (dissoc entity-schema :sales-opportunity/description)
           :entity.type/person (apply dissoc entity-schema remove-fields)
           :entity.type/oppo (apply dissoc
                                    entity-schema
                                    (conj remove-fields
                                          (when-not (= (:oppo/status entity) :oppo.status/on-hold)
                                            :oppo/on-hold-until)))                                  ;; show only when on-hold
           :entity.type/company (apply dissoc entity-schema remove-fields)
           (apply dissoc entity-schema remove-fields))
         (remove
          (fn [[k _v]]
            (let [explicit? (some #{k} remove-fields)
                  no-access? (not (state-permissions/field-read-access? k entity-type pipeline-id))]
              (or explicit? no-access?))))
         (keep (fn [[attribute field-schema]]
                 (let [visibility (or (get override-editor-types attribute)
                                      (->> field-schema
                                           :field/default-visibility
                                           :editor))

                       ;; TODO - remove when moving away from interim nev
                       visibility (if (and (= attribute :oppo/stage) @session/new-nev-in-use?)
                                    :editor.type/select
                                    visibility)]
                   (if (= visibility :editor.type/none)
                     nil
                     (merge field-schema
                            {:attribute   attribute
                             :entity-id   (:db/id entity)
                             :access/type (when-let [at (state-permissions/access-to-field-long
                                                         attribute
                                                         (:entity/type entity)
                                                         pipeline-id)]
                                            (if (some #{attribute} read-only-fields)
                                              :field.access/read
                                              at))
                             :editor/type visibility
                             :editor-type visibility}
                            (when (fn? (:field/custom-params field-schema))
                              ((:field/custom-params field-schema) entity))))))))))

(defn mandatory-field?
  [args]
  (some #{(:attribute args)} [:oppo/name :asset/name]))

(defn new-order
  [old-order moved-attribute target]
  (if (or (nil? target) (= moved-attribute target))
    old-order
    (let [order-lookup (->> old-order
                            (map-indexed (fn [i attr] [attr i]))
                            (into {}))
          move-before? (> (order-lookup moved-attribute) (order-lookup target))
          cleaned-order (remove #(= moved-attribute %) old-order)
          head (take-while #(not= % target) cleaned-order)
          tail (rest (drop-while #(not= % target) cleaned-order))
          middle (if move-before?
                   [moved-attribute target]
                   [target moved-attribute])]
      (concat head middle tail))))

(defn render-editor
  [local-atom
   {:keys [entity-atom state-atom drag-atom drag-id ordering order-config-path
           remove-fields override-editor-types view-id
           module-id custom-save! containing-entity close-modal! id->entity
           oppo-view-subscription-initial-data-loaded?]
    :as   view-args}]
  (let [entity-type (:entity/type @entity-atom)
        pipeline-id (or (fu/pipeline-id @entity-atom)
                        (:oppo/pipeline containing-entity)
                        ;;(:fundraising-opportunity/pipeline containing-entity) ;; TODO: this cannot be configured now in pipeline
                        (:sales-opportunity/pipeline containing-entity)
                        ;; no pipeline for exit-oppo/buyer: messes up permissions:
                        #_(:exit-opportunity/pipeline containing-entity))
        pipeline-attribute (pipeline/pipeline-key entity-type)
        entity-template (resolve-template {:entity                @entity-atom
                                           :tenant-schema         @session/tenant-schema
                                           :account               @session/account
                                           :role                  @session/role
                                           :pipeline-id           pipeline-id
                                           :remove-fields         remove-fields
                                           :override-editor-types override-editor-types})
        entity-template-lookup (->> entity-template
                                    (map (fn [field-schema]
                                           [(:attribute field-schema) field-schema]))
                                    (into {}))
        set-show-empty! (fn [show?] (swap! state-atom assoc :show-empty? show?))
        show-empty? (:show-empty? @state-atom)
        enriched-entity (if entity-type
                          (ic/enrich-entity @entity-atom)
                          (when @entity-atom
                            (logging/warn "Not able to resolve entity-type " @entity-atom)))
        order-config-path (or order-config-path
                              [view-id :modules :module-config module-id :ordering])
        saved-ordering (if (coll? ordering) ordering [])
        default-ordering (->> entity-template
                              (sort-by :field/default-ord)
                              (map :attribute)
                              (into []))
        remaining-default-ordering (->> default-ordering
                                        (remove #(some #{%} saved-ordering))
                                        (into []))
        field-order (concat ordering remaining-default-ordering)
        order-lookup (->> field-order
                          (map-indexed (fn [i attr] [attr i]))
                          (into {}))
        empty-fields-exist?
        (->> entity-template
             (some (fn [{:keys [:attribute] :as field-schema}]
                     (let [entity @entity-atom
                           intelligence-value (get enriched-entity attribute)
                           has-access?
                           (state-permissions/field-read-access? attribute entity-type pipeline-id)]
                       (and has-access?
                            (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                              (or (not (fn? in-use-filter?))
                                  (in-use-filter? entity
                                                  @session/entity-types
                                                  session/has-feature-access?
                                                  session/has-entity-access?)))
                            (not (:field/secondary-field? field-schema))                            ;; secondary fields are shown in related fields in editor
                            (not= (:editor/type field-schema) :editor.type/none)
                            (not= :role/system
                                  (->> field-schema
                                       :field/default-authorization
                                       :access/write))
                            (fu/empty-value? intelligence-value))))))]
    [:div
     {:class-name (fu/cs "entity-editor")}
     (doall
      (->>
       entity-template
       (sort-by (fn [field]
                  (get order-lookup (:attribute field))))
       (map
        (fn [{:keys [attribute] :as field-schema}]
          (let [entity @entity-atom
                tenant @session/tenant
                tenant-schema @session/tenant-schema
                value (fu/get-value tenant tenant-schema entity attribute)
                intelligence-value (get enriched-entity attribute)
                is-intelligence-value? (and (nil? value)
                                            (contains? enriched-entity attribute)
                                            (not (cu/blank-value?
                                                  intelligence-value)))
                has-access? (state-permissions/field-read-access? attribute entity-type pipeline-id)
                read-only?
                (not (state-permissions/field-write-access? attribute entity-type pipeline-id))
                no-value? (and (nil? value)
                               (fu/empty-value? intelligence-value))
                composite-attributes (:field/composite-attributes field-schema)
                ref-selector? (some #{(:field/data-type field-schema)}
                                    [:data.type/ref-multi :data.type/ref-single])
                restricted-info? (and oppo-view-subscription-initial-data-loaded?
                                      (not (nil? value))
                                      (nil? (get id->entity value)))
                hide-calculated-field-without-composite-value? (and (tenant-customizations/tesi?)
                                                                    (coll? composite-attributes)
                                                                    (seq composite-attributes)
                                                                    (->> composite-attributes
                                                                         (not-every? entity)))
                render-field? (and has-access?
                                   (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                                     (or (not (fn? in-use-filter?))
                                         (in-use-filter? entity
                                                         @session/entity-types
                                                         session/has-feature-access?
                                                         session/has-entity-access?)))
                                   (not (and read-only? no-value?))
                                   (not (:field/secondary-field? field-schema))                     ;; secondary fields are shown in related fields in editor
                                   (not= (:editor/type field-schema) :editor.type/none)
                                   (or show-empty?
                                       (not no-value?))
                                   (not hide-calculated-field-without-composite-value?))
                editor-args (merge
                             field-schema
                             (select-keys view-args
                                          [:module-id
                                           :menu-portal-target
                                           :close-menu-on-scroll?])
                             {:editor/type        (or (when value
                                                        (->> field-schema
                                                             :field/default-visibility
                                                             :non-empty-editor-type))
                                                      (:editor/type field-schema))
                              :state-atom         state-atom                                        ;; state is passed  from outside so debugging is easier
                              :restricted-info?   restricted-info?
                              :value              (if ref-selector?
                                                    (or (get id->entity value)
                                                        value)
                                                    (if is-intelligence-value?
                                                      intelligence-value
                                                      value))
                              :containing-entity  entity
                              :class-name         (fu/cs (:class-name field-schema)
                                                         (when no-value? "empty"))
                              :entity-types-atom  session/entity-types
                              :entity-type        entity-type
                              :entity-unique-name (entity/unique-name-or-id entity)
                              ;; TODO: take language from user / tenant config -> default to :en
                              ;; if only :en in field/translations use :en
                              :label              (or (->> field-schema
                                                           :field/translations
                                                           :en)
                                                      (loc/field-name attribute field-schema)
                                                      (loc/tr attribute))
                              ;; we select label outside of field-editor so we can customize label if needed

                              ;; can be function or boolean - most likely always view dependent
                              :mandatory?         mandatory-field?

                              ;; can pass text instead of function too
                              :info-text          (fn [_fld]
                                                    ;; we can get info text from tenant settings here
                                                    )
                              :read-only?         read-only?

                              ;:field-access [:x :w :r] ;; TODO: Fairly urgent - change the way field editors access is resolved everywhere so that the access is passed from outside
                              :open-settings!     (fn []
                                                    (modals/open-modal! options-management/modal-key
                                                                        {:pipeline-attribute pipeline-attribute
                                                                         :selected-field     attribute})
                                                    (when close-modal!
                                                      (close-modal!)))
                              :disabled?          read-only?
                              :dragging?          drag-id
                              :drop               (:drop @drag-atom)
                              :placeholder        (field/placeholder field-schema)
                              :drag-options
                              (merge
                               {:draggable?     true
                                :on-drag-start! (fn [_arg]
                                                  (swap! local-atom assoc :drag-id attribute)
                                                  (swap! drag-atom assoc :drag-id attribute))
                                :on-drag-end!   (fn [attribute]
                                                  (let [drop-target (:drag-over @local-atom)
                                                        new-order (new-order field-order attribute drop-target)]
                                                    (swap! local-atom dissoc :drag-over :drag-id)
                                                    (swap! drag-atom dissoc :drag-id)
                                                    (when (not= new-order field-order)
                                                      ;;optimistic update:
                                                      (swap! session/view-config assoc-in
                                                             order-config-path
                                                             new-order)
                                                      (account-actions/*->update-view-config
                                                       {:path      order-config-path
                                                        :new-value new-order}
                                                       identity
                                                       (fn [_]
                                                         ;;retract the optimistic update above
                                                         (when (= new-order
                                                                  (get-in @session/view-config
                                                                          order-config-path))
                                                           (swap! session/view-config assoc-in
                                                                  order-config-path
                                                                  field-order)))))))
                                :on-drag-leave  (fn [attribute]
                                                  (when (and (not= attribute (:drag-id @local-atom))
                                                             (= attribute (:drag-over @local-atom)))
                                                    (swap! local-atom dissoc :drag-over)))
                                :on-drag-enter  (fn [attribute]
                                                  (let [dragging-order (order-lookup drag-id)
                                                        drag-enter-order (order-lookup attribute)]
                                                    (when (not= attribute (:drag-id @local-atom))
                                                      (swap! local-atom assoc :drag-over attribute)
                                                      (if (> dragging-order drag-enter-order)
                                                        (swap! drag-atom assoc :drop :above)
                                                        (swap! drag-atom assoc :drop :below)))))}   ;; drag callbacks here too
                               (:drag-options view-args))
                              ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                              :tools              (concat [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy
                                                           :tool-item/open-url :tool-item/clear :tool-item/debug]
                                                          (when (and (session/is-admin?)
                                                                     (->> field-schema
                                                                          :named-value-attribute))
                                                            [:tool-item/settings]))
                              :validate!          (fn [attribute value]
                                                    (let [entity @entity-atom
                                                          schema (get entity-template-lookup attribute)
                                                          field-constraints (:field/constraints schema)
                                                          errors (constraints/validate field-constraints
                                                                                       (:db/id entity)
                                                                                       value
                                                                                       entity
                                                                                       (partial session/unique-check
                                                                                                false
                                                                                                (partial
                                                                                                 routing/open-entity))
                                                                                       loc/tr
                                                                                       logging/warn)]
                                                      errors))
                              ;; TODO: use editor-atom to store validation and save errors
                              :error-text         nil

                              :on-save!           (fn [attribute new-value on-success! on-error!]
                                                    (let [entity @entity-atom
                                                          value (fu/get-value tenant tenant-schema entity attribute)]
                                                      (when (or (not (fn? custom-save!))
                                                                (not
                                                                 (custom-save! attribute new-value on-success! on-error!)))

                                                        ;; custom
                                                        ;;make sure local state is cleared when no changes:
                                                        (if (and (= new-value value)
                                                                 (fn? on-success!))
                                                          (on-success!)
                                                          (when-not (= new-value value)
                                                            (if-let [create-value (:new-option new-value)]
                                                              (entity-actions/*->field-update-with-new-entity!
                                                               (:db/id enriched-entity)
                                                               attribute
                                                               value
                                                               create-value
                                                               (fn [_res]
                                                                 (when on-success! (on-success!)))
                                                               (fn [err]
                                                                 (when on-error! (on-error! err))))
                                                              (do
                                                                ;;optimistic save to frontend db:
                                                                (swap! entity-atom assoc attribute new-value)

                                                                (deref entity-atom)
                                                                ;; Index at once. A listing that shows
                                                                ;; this entity reads OpenSearch, and the
                                                                ;; completion tells it to refresh.
                                                                (entity-actions/*->field-update-instant-indexing!
                                                                 (:db/id enriched-entity)
                                                                 attribute
                                                                 new-value
                                                                 (fn [_res]
                                                                   (when on-success!
                                                                     (on-success!)))
                                                                 (fn [err]
                                                                   ;;retract the optimistic save above
                                                                   (when (= new-value (get @entity-atom attribute))
                                                                     (swap! entity-atom assoc attribute value)
                                                                     (deref entity-atom))
                                                                   (on-error! err))))))))))}
                             (when is-intelligence-value?                                           ;; we decide source outside
                               {:source-options {:source-icon-fn intelligence-icon/view}})
                             (when-let [secondary-attribute (:field/secondary-attribute field-schema)] ;; secondary attribute
                               {:secondary-view-args
                                (let [sec-templ (get entity-template-lookup secondary-attribute)
                                      secondary-field-schema (registry/get-field-schema tenant-schema
                                                                                        secondary-attribute)]
                                  (merge
                                   sec-templ
                                   {:state-atom state-atom
                                    :value      (if-let [_composite-attributes (:field/composite-attributes
                                                                                secondary-field-schema)]
                                                  (when-let [calc-function (:field/calculated-field
                                                                            secondary-field-schema)]
                                                    (calculated-field/calculate
                                                     {:calc-fn          calc-function
                                                      :entity-data      entity
                                                      :entity-lookup    session/get-entity
                                                      :reverse-lookup   session/reverse-lookup
                                                      :tr               loc/tr
                                                      :bigdec->float    fu/bigdec->float
                                                      :resolve-value    (partial eu/get-value
                                                                                 tenant
                                                                                 tenant-schema)
                                                      :float->bigdec    fu/float->bigdec
                                                      :get-field-schema (partial registry/get-field-schema
                                                                                 tenant-schema)
                                                      :display-string   entity/display-string}))
                                                  (or (get @entity-atom secondary-attribute)
                                                      (when (= (:field/data-type sec-templ)
                                                               :data.type/currency-symbol)
                                                        (:tenant/default-currency @session/tenant))))
                                    ;; when we have custom dynamic fields the labels come from tenant configuration
                                    :label      (loc/tr secondary-attribute)
                                    :mandatory? mandatory-field?
                                    :error-text nil}))}))]
            #_(when-not render-field?
                (logging/debug "Debug field hidden : "
                               attribute
                               " >> "
                               has-access?
                               (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                                 (or (not (fn? in-use-filter?))
                                     (in-use-filter? entity
                                                     @session/entity-types
                                                     session/has-feature-access?
                                                     session/has-entity-access?)))
                               (not (and read-only? no-value?))
                               (not hidden?)
                               (not (:field/secondary-field? field-schema))                         ;; secondary fields are shown in related fields in editor
                               (not= (:editor/type field-schema) :editor.type/none)
                               (or show-empty?
                                   (not no-value?))))
            (when render-field?
              ^{:key attribute}
              [field-editor/view editor-args]))))))
     (when (and (not (session/is-read-only-user?))
                empty-fields-exist?)
       [:div
        {:class "*:m-0 px-4 *:text-sm"}
        [button/primary-open
         {:on-click (fn [e]
                      (.preventDefault e)
                      (.stopPropagation e)
                      (set-show-empty! (not show-empty?)))}
         (if show-empty?
           (loc/tr :hide-empty-fields)
           (loc/tr :show-empty-fields))]])]))

(defn view
  [{:keys [_state-atom _drag-atom entity-id]}]
  (let [editor-atom (r/cursor app-db/app-state [:entity-editor entity-id])
        entity-atom (r/cursor session/entities [entity-id])
        drag-atom (r/cursor app-db/app-state [:entity-editor :drag entity-id])
        local-atom (atom {})]
    (r/create-class
     {:component-will-unmount (fn []
                                (swap! editor-atom dissoc-in [:entity-editor entity-id]))
      :reagent-render         (fn [args]
                                (render-editor local-atom
                                               (merge args
                                                      {:entity-atom entity-atom
                                                       :drag-id     (:drag-id @drag-atom)
                                                       :drag-atom   drag-atom
                                                       :editor-atom editor-atom
                                                       :state-atom  editor-atom})))})))
